import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { notifySlotQuotaError } from "../../utils/slotQuota.js";
import axios from "axios";
import { SquarePen, Trash } from "lucide-react";
import ObraFormModal, { ObraAssignments } from "./ObraFormModal.tsx";
import ObrasMap from "./ObrasMap.jsx";
import { Obra, ObraInput } from "../types/Obra";
import { getObras, createObra, updateObra, deleteObra } from "../api/obras.api.js";
import { getCustomerCamarasByCustomerId, updateCamaraById } from "../../customerDashboard/api/Camara.api.js";
import { getUsersByCustomerId, updateUser } from "../../customerDashboard/api/users.api.js";
import { Camara } from "../../customerDashboard/types/Camara";
import { User } from "../../customerDashboard/types/User";

interface ObrasModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
}

const ObrasModal = ({ isOpen, onClose, customerId }: ObrasModalProps) => {
  const [obras, setObras] = useState<Obra[]>([]);
  const [camaras, setCamaras] = useState<Camara[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingObra, setEditingObra] = useState<Obra | undefined>(undefined);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [obraToDelete, setObraToDelete] = useState<Obra | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedObraId, setSelectedObraId] = useState<string | null>(null);

  const fetchObras = async () => {
    setIsLoading(true);
    try {
      const response = await getObras(customerId);
      setObras(response || []);
    } catch (error) {
      console.error("Error al obtener las obras:", error);
      toast.error("Error al cargar la lista de obras.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignables = async () => {
    try {
      const [camarasResponse, usersResponse] = await Promise.all([
        getCustomerCamarasByCustomerId(customerId),
        getUsersByCustomerId(customerId),
      ]);
      setCamaras(camarasResponse || []);
      setUsers(usersResponse || []);
    } catch (error) {
      console.error("Error al obtener cámaras y usuarios:", error);
    }
  };

  useEffect(() => {
    if (isOpen && customerId) {
      fetchObras();
      fetchAssignables();
    }
  }, [isOpen, customerId]);

  const handleCreate = () => {
    setEditingObra(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (obra: Obra) => {
    setEditingObra(obra);
    setIsFormOpen(true);
  };

  const reconcileAssignments = async (obraId: string, assignments: ObraAssignments) => {
    const previousCameraIds = camaras.filter((c) => c.obra === obraId).map((c) => c.id!);
    const camarasToLink = camaras.filter(
      (c) => assignments.cameraIds.includes(c.id!) && !previousCameraIds.includes(c.id!)
    );
    const camarasToUnlink = camaras.filter(
      (c) => previousCameraIds.includes(c.id!) && !assignments.cameraIds.includes(c.id!)
    );

    const previousUserIds = users.filter((u) => (u.obra || []).includes(obraId)).map((u) => u.id!);
    const usersToLink = users.filter(
      (u) => assignments.userIds.includes(u.id!) && !previousUserIds.includes(u.id!)
    );
    const usersToUnlink = users.filter(
      (u) => previousUserIds.includes(u.id!) && !assignments.userIds.includes(u.id!)
    );

    await Promise.all([
      ...camarasToLink.map((c) => updateCamaraById(c.id, { ...c, obra: obraId })),
      ...camarasToUnlink.map((c) => updateCamaraById(c.id, { ...c, obra: null })),
      ...usersToLink.map((u) => updateUser(u.id, { ...u, obra: [...(u.obra || []), obraId] })),
      ...usersToUnlink.map((u) => updateUser(u.id, { ...u, obra: (u.obra || []).filter((id) => id !== obraId) })),
    ]);
  };

  const handleConfirm = async (data: ObraInput, assignments: ObraAssignments) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      let obraId: string;
      if (editingObra) {
        await updateObra(editingObra.id, data);
        obraId = editingObra.id;
        toast.success(`Obra "${data.nombre}" actualizada.`);
      } else {
        const created = await createObra({ ...data, customerId });
        obraId = created.id;
        toast.success(`Obra "${data.nombre}" creada.`);
      }
      await reconcileAssignments(obraId, assignments);
      setIsFormOpen(false);
      setEditingObra(undefined);
      await Promise.all([fetchObras(), fetchAssignables()]);
    } catch (error) {
      if (notifySlotQuotaError(error, toast)) {
        // Quota notice already shown.
      } else if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data?.message || error.response.data?.error || "Error al guardar la obra.");
      } else {
        console.error("Error al guardar la obra:", error);
        toast.error("Error al guardar la obra.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (obra: Obra) => {
    setObraToDelete(obra);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!obraToDelete) return;
    try {
      await deleteObra(obraToDelete.id);
      toast.success(`Obra "${obraToDelete.nombre}" eliminada.`);
      setIsDeleteModalOpen(false);
      setObraToDelete(null);
      await fetchObras();
    } catch (error) {
      console.error("Error al eliminar la obra:", error);
      toast.error("Error al eliminar la obra.");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-0 md:p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-none md:rounded-xl w-full h-full md:h-auto max-w-4xl md:mt-16 md:mb-8 shadow-lg flex flex-col">
        <div className="flex justify-between items-center border-b p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">🏗️ Obras</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Cerrar">✕</button>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(100vh-8rem)] md:max-h-[70vh]">
          <div className="flex justify-end mb-4 md:mb-6">
            <button
              onClick={handleCreate}
              className="w-full md:w-auto flex items-center justify-center px-4 py-3 md:py-2 text-white rounded-lg font-semibold transition-colors duration-200 bg-blue-600 hover:bg-blue-700 shadow-md min-h-[44px]"
            >
              Crear obra
            </button>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-8 md:p-12">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <span className="mt-3 text-sm text-gray-600">Cargando obras...</span>
            </div>
          ) : obras.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Todavía no creaste ninguna obra.</p>
          ) : (
            <>
            <div className="mb-4 md:mb-6">
              <ObrasMap obras={obras} selectedId={selectedObraId} onSelect={setSelectedObraId} />
              {!obras.some((o) => o.ubicacionMaps) && (
                <p className="text-xs text-gray-500 mt-2">Ninguna obra tiene ubicación cargada todavía. Editá una obra para ubicarla en el mapa.</p>
              )}
            </div>
            <div className="overflow-x-auto bg-white shadow-lg rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider hidden md:table-cell">Dirección</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Activa</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {obras.map((obra) => (
                    <tr
                      key={obra.id}
                      onClick={() => obra.ubicacionMaps && setSelectedObraId(obra.id)}
                      className={`transition-colors ${obra.ubicacionMaps ? "cursor-pointer" : ""} ${selectedObraId === obra.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{obra.nombre}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">{obra.direccion || "—"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className={`w-4 h-4 mx-auto rounded-full ${obra.active ? "bg-green-500" : "bg-red-500"}`} title={obra.active ? "Activa" : "Inactiva"}></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex justify-center space-x-2">
                          <button onClick={() => handleEdit(obra)} className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition duration-150" title="Editar">
                            <SquarePen className="w-5 h-5" />
                          </button>
                          <button onClick={() => handleDelete(obra)} className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition duration-150" title="Eliminar">
                            <Trash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t p-4 bg-gray-50 rounded-b-none md:rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 bg-gray-300 text-gray-800 font-medium rounded-lg hover:bg-gray-400">
            Cerrar
          </button>
        </div>
      </div>

      <ObraFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingObra(undefined);
        }}
        obraData={editingObra}
        camaras={camaras}
        users={users}
        onConfirm={handleConfirm}
        isSubmitting={isSubmitting}
      />

      {isDeleteModalOpen && obraToDelete && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[70] p-4"
          onClick={(e) => e.target === e.currentTarget && setIsDeleteModalOpen(false)}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-700 mb-6">
              ¿Confirmás eliminar la obra <span className="font-medium">{obraToDelete.nombre}</span>?
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setIsDeleteModalOpen(false)} className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">
                Cancelar
              </button>
              <button onClick={confirmDelete} className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObrasModal;
